import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { Box } from '@hivebrite/design-system/Box';

import LoginWrapper from 'hb-react/frontoffice/components/LoginWrapper';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { Price } from 'hb-react/shared/apps/Events/components/Price';
import { useEventContext } from 'hb-react/shared/apps/Events/contexts/EventContext';
import { useRegistrationFlowContext } from 'hb-react/shared/apps/Events/contexts/RegistrationFlowContext';
import { getTicketDisponibility } from 'hb-react/shared/apps/Events/utils/getTicketDisponibility';
import { isEqualCurrency } from 'hb-react/shared/apps/Events/utils/isEqualCurrency';
import {
  Hidden,
  Select,
} from 'hb-react/shared/components/formik/components/inputs';

const ReadableTicket = ({ ticket, children }) => (
  <Box
    flexDirection={{ _: 'column', m: 'row' }}
    flexGrow={2}
    alignItems={{ m: 'center' }}
  >
    <Box flexDirection="column" flexGrow={2}>
      <Box mb={1} alignItems="center">
        <Box font="h4-semibold">{ticket.name}</Box>
        <Box>
          &nbsp;
          {getTicketDisponibility(
            ticket.quantity_available,
            ticket.unlimited,
            ticket.sold_out,
            ticket.has_expired,
            ticket.limit_reached_for_current_user,
          )}
        </Box>
      </Box>
      <Box>{ticket.description}</Box>
    </Box>
    <Box pl={{ _: 0, m: 6 }} font="h4-semibold" mt={{ _: 1, m: 'unset' }}>
      <Price price={ticket.price} currency={ticket.currency} />
    </Box>
    {children}
  </Box>
);

const BuyableTicket = ({ ticket, index, onUpdate, isLoading }) => {
  const { currentUser } = useDefaultAppContexts();
  const { selectedCurrency, valuesStep1 } = useRegistrationFlowContext();
  const { event } = useEventContext();
  const { tickets } = valuesStep1;

  const {
    max_tickets_per_user: maxTicketsPerUser = 0,
    user_total_tickets: userTotalTickets = 0,
    remaining_tickets: remainingTickets = 0,
  } = event;

  const [totalSelectedTickets, ticketQuantity] = useMemo(
    () => [
      tickets
        .map(({ quantity }) => Number(quantity))
        .reduce((total, quantity) => total + quantity, 0),
      Number(tickets.find(({ id }) => id === ticket.id)?.quantity || 0),
    ],
    [tickets],
  );

  const ticketLimit = useMemo(() => {
    if (
      ticket.quantity_available_to_buy < ticket.maximum_per_person ||
      ticket.quantity_available_to_buy < maxTicketsPerUser
    ) {
      return ticket.quantity_available_to_buy;
    }

    if (ticket.maximum_per_person > 0) {
      return (
        ticket.maximum_per_person -
        (ticket.already_bought_for_current_user || 0)
      );
    }

    if (maxTicketsPerUser > 0) {
      return maxTicketsPerUser - userTotalTickets;
    }

    return ticket.quantity_available_to_buy;
  }, [userTotalTickets, maxTicketsPerUser, ticket]);

  const ticketOptions = useMemo(() => {
    const maxTickets =
      maxTicketsPerUser > 0
        ? maxTicketsPerUser - userTotalTickets
        : remainingTickets;

    const available = maxTickets - totalSelectedTickets + ticketQuantity;

    const limit = Math.min(Number(ticketLimit), available, 99);

    return Array.from(
      {
        length: limit + 1,
      },
      (_, i) => i.toString(),
    );
  }, [ticketLimit, totalSelectedTickets, ticketQuantity]);

  const isAvailableForPurchase = useMemo(() => {
    const isAvailable =
      !ticket.sold_out &&
      !ticket.has_expired &&
      !ticket.limit_reached_for_current_user;

    if (!isAvailable) {
      return false;
    }

    const selectedTickets = tickets.filter(
      ({ quantity }) => Number(quantity) > 0,
    );

    const freeTickets = selectedTickets.filter(({ isFree }) => isFree);

    if (freeTickets.length === selectedTickets.length) {
      return true;
    }

    const matchCurrency = selectedCurrency
      ? isEqualCurrency(ticket.currency, selectedCurrency)
      : true;

    return matchCurrency || ticket.isFree;
  }, [selectedCurrency, ticket, tickets]);

  return (
    <Box opacity={isAvailableForPurchase ? 1 : 0.5} width="100%">
      <ReadableTicket ticket={ticket} />
      <Box minWidth={{ _: 90, m: 154 }} justifyContent="flex-end">
        {currentUser || !ticket.internal_ticket ? (
          <Box
            font="h4-semibold"
            alignItems={{ _: 'flex-start', m: 'center' }}
            ml={{ _: 3, m: 'unset' }}
          >
            <Hidden name={`tickets[${index}][id]`} />
            <Select
              wrapperProps={{
                mb: 0,
                width: 90,
              }}
              name={`tickets[${index}][quantity]`}
              options={ticketOptions}
              onChange={({ value }) => onUpdate(ticket, { quantity: value })}
              placeholder="0"
              disabled={!isAvailableForPurchase || isLoading}
            />
          </Box>
        ) : (
          <LoginWrapper />
        )}
      </Box>
    </Box>
  );
};

const Ticket = ({ readOnly, ...props }) =>
  readOnly ? <ReadableTicket {...props} /> : <BuyableTicket {...props} />;

Ticket.propTypes = {
  readOnly: PropTypes.bool,
  onUpdate: PropTypes.func,
  maxTicketsPerUser: PropTypes.number,
  quantity_available_to_buy: PropTypes.number,
};

Ticket.defaultProps = {
  readOnly: false,
};

export default Ticket;
